import {
    AuthReference,
    AuthReferenceId,
    AuthOnBehalfOfConfig,
    Token,
} from '../auth';
import {
    Error400Response,
    Error401Response,
    Error403Response,
    Error409Response,
} from './errors';

type CartItem = {
    quantity: number;
    allowSubstitues: boolean;
    specialInstructions: string;
    upc: string;
};

type Cart = {
    id: string;
    items: CartItem[];
    name: string;
};

type CreateCartResponse = {
    data: Cart[];
    meta: any;
};

type AddToCartResponse = {
    data: Cart[];
    meta: any;
};

type GetRequestErrorResult =
    | Error
    | Error400Response
    | Error401Response
    | Error403Response
    | Error409Response;

/**
 * Casts the data from the response to the correct error type
 * @param response Response from Axios
 */
const getRequestError = (response: Response): GetRequestErrorResult => {
    switch (response.status) {
        case 400:
            return {
                status: 400,
                timestamp: Date.now(),
                code: '400',
                reason: 'Bad Request',
            } as Error400Response;
        case 401:
            return {} as Error401Response;
        case 403:
            return {} as Error403Response;
        case 409:
            return {} as Error409Response;
        case 500:
            return new Error('[KROGER API] Internal Server Error');
        default:
            return new Error(`[KROGER API] ${response.statusText}`);
    }
};

/**
 * Creates a fetch Header and sets to what we need
 * @param token Auth token
 */
const getRequestConfig = (token: Token): Headers => {
    const headers = new Headers();
    headers.set('Authorization', `bearer ${token.accessToken}`);
    headers.set('Content-Type', 'application/json; charset=utf-8');
    return headers;
};

/**
 * Returns the properties necessary to send a user to Kroger to authorize the application
 * @param referenceId The ID to use when asking BED for Auth result
 * @param config Kroger Auth config
 */
const getKrogerAuthReference = (
    referenceId: AuthReferenceId,
    config: AuthOnBehalfOfConfig
): AuthReference => {
    const redirectUri = `${config.redirectUri}?id=${referenceId}`;
    const uri =
        `${config.baseUri}?client_id=${encodeURIComponent(config.clientId)}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=${config.responseType}` +
        `&scope=${config.scope}`;

    return {
        uri,
        referenceId,
    };
};

type CreateCartResult = Promise<
    [
        boolean,
        (
            | Error
            | Error400Response
            | Error401Response
            | Error403Response
            | Error409Response
            | null
        )
    ]
>;
/**
 * Creates a cart for the user and adds the items to the cart
 * @param items Cart Itmes to add to the new cart
 * @param baseUri Kroger base URI
 * @param token Auth tokens for the user
 * @param axios Axios reference to make the API requests
 */
const createCart = async (
    items: CartItem[],
    token: Token,
    apiHost: string = 'https://live.polaris.me'
): CreateCartResult => {
    const cartUrl = `${apiHost}/api/products/v1/integrations/kro/cart`;
    const data = { items, token: token.accessToken };
    const headers = getRequestConfig(token);
    const reqIni = {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
    } as RequestInit;
    const request = new Request(cartUrl, reqIni);
    const response = await fetch(request);
    if (response.status === 204) {
        return [true, null];
    }
    const error = getRequestError(response);
    return [false, error];
};

export {
    AddToCartResponse,
    Cart,
    CartItem,
    CreateCartResponse,
    CreateCartResult,
    getKrogerAuthReference,
    createCart,
};
